'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CategoriesModal } from '../components/categories-modal';
import { groupTreeNodesToCategoryTrees } from '../lib/group-tree-to-category-tree';
import type { CategoryTree, GroupTreeNode } from '../types';
interface ModalCategoriesContextValues {
  headcountCategories: CategoryTree[];
  nonHeadcountCategories: CategoryTree[];
  hasNoCategories: boolean;
  hasNestedCategories: boolean;
  areAllExpanded: boolean;
  toggleAllCategories: () => void;
  handleHeadcountValueChange: (newValue: string[]) => void;
  handleNonHeadcountValueChange: (newValue: string[]) => void;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}
const ModalCategoriesContext = createContext<ModalCategoriesContextValues | undefined>(undefined);
export interface CategoriesTreeShape {
  headcount: GroupTreeNode[];
  nonHeadcount: GroupTreeNode[];
}
interface ModalCategoriesProviderProps {
  children: React.ReactNode;
  categoriesTree: CategoriesTreeShape;
}
function ModalCategoriesProvider(t0) {
  const $ = _c(43);
  const {
    children,
    categoriesTree
  } = t0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expandedState, setExpandedState] = useState(t1);
  let t2;
  if ($[1] !== expandedState) {
    t2 = new Map(Object.entries(expandedState).map(_temp));
    $[1] = expandedState;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const expandedStateMap = t2;
  let t3;
  if ($[3] !== categoriesTree.headcount || $[4] !== expandedStateMap) {
    t3 = groupTreeNodesToCategoryTrees(categoriesTree.headcount, true, expandedStateMap);
    $[3] = categoriesTree.headcount;
    $[4] = expandedStateMap;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const headcountCategories = t3;
  let t4;
  if ($[6] !== categoriesTree.nonHeadcount || $[7] !== expandedStateMap) {
    t4 = groupTreeNodesToCategoryTrees(categoriesTree.nonHeadcount, false, expandedStateMap);
    $[6] = categoriesTree.nonHeadcount;
    $[7] = expandedStateMap;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const nonHeadcountCategories = t4;
  const hasNoCategories = headcountCategories.length === 0 && nonHeadcountCategories.length === 0;
  let t5;
  if ($[9] !== hasNoCategories || $[10] !== headcountCategories || $[11] !== nonHeadcountCategories) {
    t5 = !hasNoCategories && (headcountCategories.some(_temp2) || nonHeadcountCategories.some(_temp3));
    $[9] = hasNoCategories;
    $[10] = headcountCategories;
    $[11] = nonHeadcountCategories;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const hasNestedCategories = t5;
  let t6;
  if ($[13] !== categoriesTree.headcount || $[14] !== categoriesTree.nonHeadcount) {
    t6 = [...categoriesTree.headcount.filter(_temp4).map(_temp5), ...categoriesTree.nonHeadcount.filter(_temp6).map(_temp7)];
    $[13] = categoriesTree.headcount;
    $[14] = categoriesTree.nonHeadcount;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const expandableCategoryIds = t6;
  let t7;
  if ($[16] !== expandableCategoryIds || $[17] !== expandedState) {
    t7 = expandableCategoryIds.length > 0 && expandableCategoryIds.every(id_0 => expandedState[id_0]);
    $[16] = expandableCategoryIds;
    $[17] = expandedState;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const areAllExpanded = t7;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (categoryId, isCollapsed) => {
      setExpandedState(prev => ({
        ...prev,
        [categoryId]: !isCollapsed
      }));
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const setCategoryExpandedValue = t8;
  let t9;
  if ($[20] !== areAllExpanded || $[21] !== expandableCategoryIds) {
    t9 = () => {
      const nextExpanded = !areAllExpanded;
      setExpandedState(prev_0 => {
        const next = {
          ...prev_0
        };
        for (const id_1 of expandableCategoryIds) {
          next[id_1] = nextExpanded;
        }
        return next;
      });
    };
    $[20] = areAllExpanded;
    $[21] = expandableCategoryIds;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const toggleAllCategories = t9;
  let t10;
  if ($[23] !== headcountCategories) {
    t10 = newValue => {
      headcountCategories.forEach(category_1 => {
        const isOpen = newValue.includes(category_1.id);
        setCategoryExpandedValue(category_1.id, !isOpen);
      });
    };
    $[23] = headcountCategories;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleHeadcountValueChange = t10;
  let t11;
  if ($[25] !== nonHeadcountCategories) {
    t11 = newValue_0 => {
      nonHeadcountCategories.forEach(category_2 => {
        const isOpen_0 = newValue_0.includes(category_2.id);
        setCategoryExpandedValue(category_2.id, !isOpen_0);
      });
    };
    $[25] = nonHeadcountCategories;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const handleNonHeadcountValueChange = t11;
  let t12;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setIsModalOpen(true);
    };
    t13 = () => {
      setIsModalOpen(false);
    };
    $[27] = t12;
    $[28] = t13;
  } else {
    t12 = $[27];
    t13 = $[28];
  }
  let t14;
  if ($[29] !== areAllExpanded || $[30] !== handleHeadcountValueChange || $[31] !== handleNonHeadcountValueChange || $[32] !== hasNestedCategories || $[33] !== hasNoCategories || $[34] !== headcountCategories || $[35] !== isModalOpen || $[36] !== nonHeadcountCategories || $[37] !== toggleAllCategories) {
    t14 = {
      headcountCategories,
      nonHeadcountCategories,
      hasNoCategories,
      hasNestedCategories,
      areAllExpanded,
      toggleAllCategories,
      handleHeadcountValueChange,
      handleNonHeadcountValueChange,
      isModalOpen,
      openModal: t12,
      closeModal: t13
    };
    $[29] = areAllExpanded;
    $[30] = handleHeadcountValueChange;
    $[31] = handleNonHeadcountValueChange;
    $[32] = hasNestedCategories;
    $[33] = hasNoCategories;
    $[34] = headcountCategories;
    $[35] = isModalOpen;
    $[36] = nonHeadcountCategories;
    $[37] = toggleAllCategories;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const values = t14;
  let t15;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <CategoriesModal />;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== children || $[41] !== values) {
    t16 = <ModalCategoriesContext.Provider value={values}>{children}{t15}</ModalCategoriesContext.Provider>;
    $[40] = children;
    $[41] = values;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
}
function _temp7(n_2) {
  return n_2.id;
}
function _temp6(n_1) {
  return n_1.children.length > 0;
}
function _temp5(n_0) {
  return n_0.id;
}
function _temp4(n) {
  return n.children.length > 0;
}
function _temp3(category) {
  return category.children.length > 0;
}
function _temp2(category_0) {
  return category_0.children.length > 0;
}
function _temp(t0) {
  const [id, isExpanded] = t0;
  return [id, !isExpanded] as const;
}
function useModalCategories() {
  const context = useContext(ModalCategoriesContext);
  if (!context) {
    throw new Error("useModalCategories must be used within a ModalCategoriesProvider");
  }
  return context;
}
export { ModalCategoriesProvider, useModalCategories };